import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Loader2, Info, RefreshCcw,
    ChevronLeft, Printer, Download,
    TrendingUp, TrendingDown, Wallet,
    Store, CalendarDays
} from 'lucide-react';
import { motion } from 'framer-motion';

import Button from '../components/ui/Button';
import { useToast } from '../hooks/useToast';
import { 
    fetchPNLReport, 
    currencyCodeToSymbolMap, 
    fetchStoresWhereStaffAssigned 
} from '../apiHelper';

const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

const formatCurrency = (amount, currencyCode) => {
    const symbol = currencyCodeToSymbolMap[currencyCode] || '₦';
    return `${symbol}${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Math.abs(amount))}`;
};

const NewPNLReport = ({ onBack }) => {
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [orgName, setOrgName] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Filter State
    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    // Initial Load: User and Stores
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (lcUser) {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);
            initializeStores(parsedUser);
        } else {
            showToast("User not found. Please log in.", "error");
            navigate('/login');
        }
    }, [navigate, showToast]);

    const initializeStores = async (parsedUser) => {
        try {
            const fetchedStores = await fetchStoresWhereStaffAssigned(parsedUser);
            const options = fetchedStores
                .filter(store => store.storetype !== 'WHS')
                .map(store => ({
                    label: store.identifier,
                    value: store.store_id,
                }));
            setStores(options);
        } catch (err) {
            showToast("Failed to load assigned stores", "error");
        }
    };

    const loadReport = useMemo(() => {
        if (!user) return null;

        return async () => {
            setLoading(true);
            setError(null);
            try {
                const params = {
                    users_id: user.users_id,
                    start_date: startDate,
                    end_date: endDate,
                    store_id: selectedStoreId || null,
                };
                const response = await fetchPNLReport(params);
                setOrgName(response.organization_name);
                setReportData(response.pnl_report);
            } catch (err) {
                setError("Failed to load report. Please try again.");
                showToast("Failed to load report.", "error");
            } finally {
                setLoading(false);
            }
        };
    }, [user, startDate, endDate, selectedStoreId, showToast]);

    useEffect(() => {
        if (loadReport) loadReport();
    }, [loadReport]);

    const handleRowClick = (account) => {
        navigate('/reports/account-ledger-report', {
            state: { account_id: account.account_id }
        });
    };

    return (
        <div className="min-h-screen bg-[#F9FAFB] pb-12">
            <nav className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-zinc-200 px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <button
                        onClick={onBack || (() => navigate(-1))}
                        className="flex items-center text-zinc-500 hover:text-zinc-900 transition-colors font-medium text-sm gap-2"
                    >
                        <ChevronLeft size={18} /> Back to Reports
                    </button>
                    <div className="flex items-center gap-3">
                        <Button variant="secondary" className="flex items-center justify-center bg-white border-zinc-200 text-zinc-700 shadow-sm px-4">
                            <Printer size={16} className="mr-2" /> Print
                        </Button>
                        <Button variant="primary" className="flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 shadow-sm px-4">
                            <Download size={16} className="mr-2" /> Export PDF
                        </Button>
                    </div>
                </div>
            </nav>

            <main className="max-w-7xl mx-auto px-6 mt-8 space-y-6">
                <div className="bg-white rounded-2xl border border-zinc-200 p-8 shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-2">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2 text-indigo-600 font-semibold text-xs uppercase tracking-widest">
                                <TrendingUp size={14} /> Performance Report
                            </div>
                            <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">Profit & Loss</h1>
                            <p className="text-zinc-500 font-medium">{orgName || 'All Organizations'}</p>
                        </div>

                        {reportData && (
                            <div className={`flex items-center gap-3 px-4 py-2 rounded-xl border ${reportData.net_income >= 0
                                ? 'bg-emerald-50 border-emerald-100 text-emerald-700'
                                : 'bg-rose-50 border-rose-100 text-rose-700'
                                }`}>
                                {reportData.net_income >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                                <div className="text-sm">
                                    <p className="font-bold leading-none">
                                        {reportData.net_income >= 0 ? 'Net Profit' : 'Net Loss'}
                                    </p>
                                    <p className="text-[11px] opacity-80">Final Bottom Line</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-4">
                        {/* Branch Selector */}
                        <div className="flex items-center gap-2 bg-zinc-50 p-2 rounded-xl border border-zinc-100 min-w-[200px]">
                            <Store size={16} className="text-zinc-400 ml-2" />
                            <select 
                                value={selectedStoreId}
                                onChange={(e) => setSelectedStoreId(e.target.value)}
                                className="bg-transparent text-sm font-semibold text-zinc-700 focus:outline-none border-none w-full cursor-pointer"
                            >
                                <option value="">All Branches</option>
                                {stores.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Date Range Picker */}
                        <div className="flex items-center gap-3 bg-zinc-50 p-2 rounded-xl border border-zinc-100">
                            <div className="flex items-center gap-2">
                                <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider px-2">From</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="bg-transparent text-sm font-semibold text-zinc-700 focus:outline-none border-none p-0 cursor-pointer"
                                />
                            </div>
                            <div className="h-4 w-[1px] bg-zinc-200" />
                            <div className="flex items-center gap-2">
                                <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider px-2">To</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="bg-transparent text-sm font-semibold text-zinc-700 focus:outline-none border-none p-0 cursor-pointer"
                                />
                            </div>
                        </div>

                        <button
                            onClick={loadReport}
                            className="md:ml-auto flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-colors"
                        >
                            <RefreshCcw size={16} className={loading ? 'animate-spin' : ''} />
                            <span className="text-xs font-bold uppercase">Refresh</span>
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-zinc-50/50 border-b border-zinc-200">
                                <th className="px-8 py-4 text-[11px] font-bold text-zinc-500 uppercase tracking-widest">Account</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-zinc-500 uppercase tracking-widest">Code</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-zinc-500 uppercase tracking-widest text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="3" className="px-8 py-20 text-center">
                                        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto mb-2" />
                                        <p className="text-zinc-400 font-medium">Analyzing financials...</p>
                                    </td>
                                </tr>
                            ) : !reportData ? (
                                <tr>
                                    <td colSpan="3" className="px-8 py-20 text-center text-zinc-400 italic">No data found for this period.</td>
                                </tr>
                            ) : (
                                <>
                                    {/* Revenue Section */}
                                    <tr className="bg-zinc-50/30">
                                        <td colSpan="3" className="px-8 py-3 text-[10px] font-bold text-indigo-600 uppercase tracking-[0.2em]">Revenue</td>
                                    </tr>
                                    {reportData.details.revenue_accounts.map((account) => (
                                        <motion.tr 
                                            key={account.account_id}
                                            onClick={() => handleRowClick(account)}
                                            className="cursor-pointer hover:bg-zinc-50 transition-colors group"
                                        >
                                            <td className="px-8 py-4 text-sm font-bold text-zinc-900 group-hover:text-indigo-600">{account.name}</td>
                                            <td className="px-6 py-4 text-xs font-mono text-zinc-400">{account.account_code}</td>
                                            <td className="px-6 py-4 text-sm font-mono text-right text-zinc-700">
                                                {formatCurrency(account.amount, reportData.currency_code)}
                                            </td>
                                        </motion.tr>
                                    ))}
                                    <tr className="bg-zinc-50 font-bold border-t border-zinc-200">
                                        <td colSpan="2" className="px-8 py-4 text-sm text-zinc-900">Total Revenue</td>
                                        <td className="px-6 py-4 text-sm font-mono text-right text-zinc-900">
                                            {formatCurrency(reportData.total_revenue, reportData.currency_code)}
                                        </td>
                                    </tr>

                                    {/* Expenses Section */}
                                    <tr className="bg-zinc-50/30">
                                        <td colSpan="3" className="px-8 py-3 text-[10px] font-bold text-rose-600 uppercase tracking-[0.2em]">Expenses</td>
                                    </tr>
                                    {reportData.details.expense_accounts.map((account) => (
                                        <motion.tr 
                                            key={account.account_id}
                                            onClick={() => handleRowClick(account)}
                                            className="cursor-pointer hover:bg-zinc-50 transition-colors group"
                                        >
                                            <td className="px-8 py-4 text-sm font-bold text-zinc-900 group-hover:text-indigo-600">{account.name}</td>
                                            <td className="px-6 py-4 text-xs font-mono text-zinc-400">{account.account_code}</td>
                                            <td className="px-6 py-4 text-sm font-mono text-right text-zinc-700">
                                                ({formatCurrency(account.amount, reportData.currency_code)})
                                            </td>
                                        </motion.tr>
                                    ))}
                                    <tr className="bg-zinc-50 font-bold border-t border-zinc-200">
                                        <td colSpan="2" className="px-8 py-4 text-sm text-zinc-900">Total Expenses</td>
                                        <td className="px-6 py-4 text-sm font-mono text-right text-rose-600">
                                            ({formatCurrency(reportData.total_expenses, reportData.currency_code)})
                                        </td>
                                    </tr>
                                </>
                            )}
                        </tbody>
                        <tfoot className="bg-zinc-900">
                            <tr>
                                <td colSpan="2" className="px-8 py-6 text-sm font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
                                    <Wallet size={16} /> Net Income
                                </td>
                                <td className="px-6 py-6 text-2xl font-mono text-right text-white font-bold border-l border-white/10">
                                    {reportData ? formatCurrency(reportData.net_income, reportData.currency_code) : '-'}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div className="flex items-center gap-2 text-zinc-400 px-2">
                    <Info size={14} />
                    <p className="text-[11px] font-medium">This Profit and Loss statement calculates net performance using the accrual method of accounting.</p>
                </div>
            </main>
        </div>
    );
};

export default NewPNLReport;